import { GridLegacy as Grid, useTheme } from "@mui/material";
import { FormTextField } from "components/Forms/FormTextField";
import { RadioGroupField } from "components/Forms/RadioGroupField";
import { StatePicker } from "components/StatePicker";
import { EncampMailingAddress } from "encamp-shared/src/constants/encampAddress";
import { useValidatingFormContext } from "hooks/useValidatingForm";
import { useEffect } from "react";

import { CountryPicker } from "./CountryPicker";

export function MailingAddress({ readOnly }: { readOnly?: boolean }) {
  const theme = useTheme();
  const { control, getFieldState, metadata, setValue, watch } = useValidatingFormContext();

  const isMailingAddressSameAsFacility = watch("isMailingAddressSameAsFacility");
  const streetAddress1 = watch("streetAddress1");
  const streetAddress2 = watch("streetAddress2");
  const city = watch("city");
  const state = watch("state");
  const zip = watch("zip");
  const country = watch("country");
  const companyName = watch("companyName");

  const disabled =
    isMailingAddressSameAsFacility === "encamp" || isMailingAddressSameAsFacility || readOnly;

  useEffect(() => {
    if (isMailingAddressSameAsFacility === "encamp") {
      setValue("mailingStreetAddress1", EncampMailingAddress.mailingStreetAddress1);
      setValue("mailingStreetAddress2", EncampMailingAddress.mailingStreetAddress2);
      setValue("mailingCity", EncampMailingAddress.mailingCity);
      setValue("mailingState", EncampMailingAddress.mailingState);
      setValue("mailingZip", EncampMailingAddress.mailingZip);
      setValue("mailingCountry", EncampMailingAddress.mailingCountry);
      setValue("mailingCompanyName", companyName);
    } else if (isMailingAddressSameAsFacility) {
      setValue("mailingStreetAddress1", streetAddress1);
      setValue("mailingStreetAddress2", streetAddress2);
      setValue("mailingCity", city);
      setValue("mailingState", state);
      setValue("mailingZip", zip);
      setValue("mailingCountry", country);
      setValue("mailingCompanyName", companyName);
    } else if (
      getFieldState("isMailingAddressSameAsFacility").isTouched &&
      !isMailingAddressSameAsFacility
    ) {
      setValue("mailingStreetAddress1", null);
      setValue("mailingStreetAddress2", null);
      setValue("mailingCity", null);
      setValue("mailingState", null);
      setValue("mailingZip", null);
      setValue("mailingCountry", null);
      setValue("mailingCompanyName", null);
    }
  }, [
    isMailingAddressSameAsFacility,
    setValue,
    getFieldState,
    streetAddress1,
    streetAddress2,
    city,
    state,
    zip,
    country,
    companyName,
  ]);

  return (
    <Grid
      container
      columnSpacing={theme.spacing(3)}
      rowSpacing={theme.spacing(metadata?.isDisplayOnly ? 3 : 1)}
      marginTop={theme.spacing(metadata?.isDisplayOnly ? 0 : 2)}
    >
      <Grid item xs={12} mb={1}>
        <RadioGroupField
          label="Is the mailing address the same as the facility address?"
          name="isMailingAddressSameAsFacility"
          control={control}
          disabled={readOnly}
          radioOptions={[
            {
              label: "Yes",
              value: true,
            },
            {
              label: "No",
              value: false,
            },
            {
              label: "Use Encamp's mailing address",
              value: "encamp",
            },
          ]}
        />
      </Grid>

      <Grid item xs={12} lg={4}>
        <FormTextField
          disabled={disabled}
          name="mailingStreetAddress1"
          label="Street Address"
          control={control}
        />
      </Grid>
      <Grid item xs={12} lg={4}>
        <FormTextField
          disabled={disabled}
          name="mailingStreetAddress2"
          label="Street Address 2"
          control={control}
        />
      </Grid>
      <Grid item xs={0} lg={4} />

      <Grid item xs={12} lg={4}>
        <FormTextField disabled={disabled} name="mailingCity" label="City" control={control} />
      </Grid>
      <Grid item xs={12} lg={4}>
        <StatePicker
          name="mailingState"
          countryFieldName="mailingCountry"
          control={control}
          disabled={disabled}
        />
      </Grid>
      <Grid item xs={12} lg={4}>
        <FormTextField name="mailingZip" label="Zip" disabled={disabled} control={control} />
      </Grid>

      <Grid item xs={12} lg={4}>
        <CountryPicker name="mailingCountry" control={control} disabled={disabled} />
      </Grid>
      <Grid item xs={12} lg={4}>
        <FormTextField
          name="mailingCompanyName"
          label="Company Name"
          control={control}
          disabled={disabled}
        />
      </Grid>
    </Grid>
  );
}
